import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Car, User, EyeOff, Eye, Send, ShoppingCart, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const VehiclesPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishVehicle, setPublishVehicle] = useState(null);
  const navigate = useNavigate();
  const isAdmin = auth?.admin?.role === 'admin' || auth?.admin?.is_admin === true;

  useEffect(() => { loadVehicles(); }, [search, showInactive]);

  const loadVehicles = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (showInactive) params.show_inactive = true;
      const res = await axios.get(`${API}/vehicles`, { params });
      setVehicles(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const toggleVehicleActive = async (e, vehicleId) => {
    e.stopPropagation();
    try {
      const res = await axios.patch(`${API}/vehicles/${vehicleId}/toggle-active`);
      toast.success(res.data.message);
      loadVehicles();
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900">{t('vehicles.title')}</h1>
          <p className="text-base text-slate-600 mt-1">{vehicles.length} véhicule(s)</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par marque, modèle, plaque..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="vehicle-search"
            />
          </div>
          {isAdmin && (
            <Button
              variant={showInactive ? "default" : "outline"}
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              data-testid="toggle-inactive-vehicles"
            >
              {showInactive ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
              {showInactive ? 'Tous' : 'Voir inactifs'}
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-8">{t('common.loading')}</p>
        ) : vehicles.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <Car className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{search ? 'Aucun résultat' : 'Aucun véhicule'}</p>
          </Card>
        ) : (
          <div className="grid gap-3">
            {vehicles.map((v) => (
              <Card
                key={v.id}
                className={`p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${v.active === false ? 'border-orange-200 bg-orange-50/30 opacity-70' : 'border-slate-200'}`}
                onClick={() => navigate(`/admin/clients/${v.client_id}`)}
                data-testid={`vehicle-list-${v.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${v.active === false ? 'bg-orange-100 text-orange-600' : 'bg-indigo-100 text-indigo-700'}`}>
                    <Car className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {v.active === false && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium">Inactif</span>}
                      <p className="text-sm font-bold text-slate-900">{v.brand} {v.model}</p>
                      <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{v.plate}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {v.client_name}</span>
                      {v.mileage > 0 && <span>{v.mileage.toLocaleString()} km</span>}
                      {v.type && <span>{v.type}</span>}
                    </div>
                  </div>
                  {isAdmin ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={(e) => { e.stopPropagation(); setPublishVehicle(v); }}
                        data-testid={`publish-vehicle-${v.id}`}
                        title="Publier sur la Marketplace"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={v.active === false ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'}
                        onClick={(e) => toggleVehicleActive(e, v.id)}
                        data-testid={`toggle-vehicle-${v.id}`}
                      >
                        {v.active === false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await axios.post(`${API}/deletion-requests`, {
                            type: 'deactivate_vehicle',
                            target_id: v.id,
                            target_label: `${v.brand} ${v.model} (${v.plate})`
                          });
                          toast.success('Demande de désactivation envoyée à l\'administrateur');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                      }}
                      data-testid={`request-deactivate-vehicle-${v.id}`}
                      title="Demander la désactivation"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      {publishVehicle && <PublishModal vehicle={publishVehicle} onClose={() => setPublishVehicle(null)} navigate={navigate} />}
    </AdminLayout>
  );
};

const PublishModal = ({ vehicle, onClose, navigate }) => {
  const [prices, setPrices] = useState({ price_public: '', price_pro: '', internal_margin: '', description: '' });
  const [saving, setSaving] = useState(false);

  const handlePublish = async () => {
    if (!prices.price_pro) { toast.error('Prix professionnel requis'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/marketplace/publish-vehicle/${vehicle.id}`, {
        ...prices,
        price_public: parseFloat(prices.price_public) || 0,
        price_pro: parseFloat(prices.price_pro) || 0,
        internal_margin: parseFloat(prices.internal_margin) || 0,
      });
      toast.success('Vehicule publie ! Ajoutez des photos pour activer l\'annonce.');
      onClose();
      navigate(`/admin/marketplace?tab=my`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="publish-vehicle-modal">
      <Card className="w-full max-w-md mx-4 p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Publier sur la Marketplace</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{vehicle.brand} {vehicle.model} ({vehicle.plate})</p>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-500 mb-1 block">Prix vente public (CHF)</label><Input type="number" value={prices.price_public} onChange={e => setPrices({...prices, price_public: e.target.value})} placeholder="15000" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Prix professionnel inter-garage (CHF) *</label><Input type="number" value={prices.price_pro} onChange={e => setPrices({...prices, price_pro: e.target.value})} placeholder="12000" data-testid="publish-price-pro" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">Marge interne (CHF)</label><Input type="number" value={prices.internal_margin} onChange={e => setPrices({...prices, internal_margin: e.target.value})} placeholder="2000" /></div>
          <div><label className="text-xs text-slate-500 mb-1 block">{t('common.description')}</label><textarea value={prices.description} onChange={e => setPrices({...prices, description: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[60px] bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-white" placeholder="Bon etat, revision complete..." /></div>
          <p className="text-xs text-amber-600">Les photos seront ajoutees apres dans la Marketplace. Obligatoires pour activer l'annonce.</p>
          <Button onClick={handlePublish} disabled={saving} className="w-full" data-testid="confirm-publish-btn">{saving ? 'Publication...' : 'Publier'}</Button>
        </div>
      </Card>
    </div>
  );
};

export default VehiclesPage;
